import { Flag } from "lucide-react";
import { Link } from "react-router-dom";
import type { MilestoneRow } from "../types";
import {
  Card,
  CardHeader,
  Empty,
  formatDateShort,
  SkeletonRow,
  ViewAllLink,
} from "./_shared";

type Props = {
  milestones: MilestoneRow[];
  isLoading?: boolean;
};

function dueColor(iso: string | null): string {
  if (!iso) return "hsl(215,16%,47%)";
  const diffDays = Math.floor(
    (new Date(iso).getTime() - Date.now()) / (24 * 3600 * 1000),
  );
  if (diffDays < 7) return "hsl(0,70%,45%)";
  if (diffDays < 30) return "hsl(35,90%,35%)";
  return "hsl(215,16%,47%)";
}

export function UpcomingMilestonesWidget({ milestones, isLoading }: Props) {
  return (
    <Card>
      <CardHeader
        icon={
          <Flag
            className="h-4 w-4 text-[hsl(142,72%,28%)]"
            strokeWidth={1.5}
          />
        }
        title="Upcoming Milestones"
        count={milestones.length}
        action={<ViewAllLink href="/milestones" />}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : milestones.length === 0 ? (
        <Empty
          icon={
            <Flag
              className="h-8 w-8 text-muted-foreground opacity-40"
              strokeWidth={1.5}
            />
          }
          text="No upcoming milestones"
        />
      ) : (
        <ul className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
          {milestones.slice(0, 5).map((m) => (
            <li key={m.id}>
              <Link
                to={`/milestones/${m.id}`}
                className="flex items-center gap-2 py-2 hover:bg-[hsl(210,50%,98%)]"
              >
                <Flag
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: dueColor(m.dueDate) }}
                  strokeWidth={1.5}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-foreground">
                    {m.title}
                  </div>
                  {m.projectName && (
                    <div className="truncate text-[11px] text-muted-foreground">
                      {m.projectName}
                    </div>
                  )}
                </div>
                <span
                  className="shrink-0 text-[11px] font-medium"
                  style={{ color: dueColor(m.dueDate) }}
                >
                  {formatDateShort(m.dueDate)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
